import { RequestStatusBadge } from "@/components/common/section-components/badge/RequestStatusBadge";
import { SuppliesOrderStatus } from "@/constants/entity-status";
import {
  SuppliesOrderAttemptsModal,
  SuppliesOrderAttempt,
} from "@/components/modal/SuppliesOrderAttemptsModal";
import { SuppliesOrderCancelMotiveModal } from "@/components/modal/SuppliesOrderCancelMotiveModal";
import { MenuComponent } from "@/components/menu/menu";
import {
  canMarkSuppliesOrderAsDelivered,
  isSuppliesOrderEditable,
  isSuppliesOrderInTransit,
} from "@/utils/supplies-order-status";
import { AppColors } from "@/styles/colors";
import { Badge, Button, Flex, Grid, GridItem, Text } from "@chakra-ui/react";
import { Ban, Check, ChevronDown, PenLine } from "lucide-react";
import { useMemo, useState } from "react";

const normalizeStatus = (raw?: string) =>
  String(raw ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replaceAll(/\p{Diacritic}/gu, "");

const canShowReassignAction = (item: any) =>
  item?.estado === SuppliesOrderStatus.NOT_DELIVERED ||
  ((!!item?.chofer || !!item?.esMostrador) &&
    !isSuppliesOrderInTransit(item?.estado) &&
    item?.estado !== SuppliesOrderStatus.DELIVERED);

const canCancelOrderStatus = (item: any) =>
  isSuppliesOrderEditable(item?.estado);

export interface FuelSlotRequestOrdersTableItem {
  id: number;
  nroPedido?: string;
  cantidad?: number;
  destino?: string;
  lugarDespacho?: string;
  nombreDespacho?: string;
  estado?: string;
  cantidadTotal?: number;
  intentos?: SuppliesOrderAttempt[];
  chofer?: string | null;
  esMostrador?: boolean | null;
  motivoCancelacion?: string | null;
  fechaCancelacion?: string | null;
  usuarioCancela?: string | null;
}

type FuelOrdersTableComputedItem = FuelSlotRequestOrdersTableItem & {
  litros: string;
  despacho: string;
  status: SuppliesOrderStatus;
  currentIntento: number;
  intentos: SuppliesOrderAttempt[];
};

interface Props {
  orders: FuelSlotRequestOrdersTableItem[];
  loadingConfirmOrderId: number | null;
  onConfirmOrder: (orderId: number) => void;
  showEditOrders?: boolean;
  onEditOrders?: () => void;
  assignDriverBlocked?: boolean;
  reassignDriverBlocked?: boolean;
  onCancelOrder: (orderId: number) => void;
  loadingCancelOrderId: number | null;
  cancelOrderBlocked?: boolean;
  onDeliverOrder?: (orderId: number) => void;
  loadingDeliverOrderId?: number | null;
  deliverOrderBlocked?: boolean;
}

interface ConfirmOrderButtonProps {
  id: number;
  loadingConfirmOrderId: number | null;
  onConfirmOrder: (orderId: number) => void;
}

const ConfirmOrderButton = ({
  id,
  loadingConfirmOrderId,
  onConfirmOrder,
}: ConfirmOrderButtonProps) => {
  return (
    <Button
      height="28px"
      bgColor={AppColors.GREEN_PRIMARY}
      onClick={() => onConfirmOrder(id)}
      loading={loadingConfirmOrderId === id}
      aria-label="Asignar pedido"
      padding="8px 12px"
    >
      <Check size={12} />
      <Text fontWeight={600} fontSize="14px">
        Asignar pedido
      </Text>
    </Button>
  );
};

const ReassignOrderButton = ({
  id,
  loadingConfirmOrderId,
  onConfirmOrder,
}: ConfirmOrderButtonProps) => {
  return (
    <Button
      height="28px"
      bgColor={AppColors.GREEN_PRIMARY}
      onClick={() => onConfirmOrder(id)}
      loading={loadingConfirmOrderId === id}
      aria-label="Reasignar pedido"
      padding="8px 12px"
    >
      <Check size={12} />
      <Text fontWeight={600} fontSize="14px">
        Reasignar pedido
      </Text>
    </Button>
  );
};

interface DeliverOrderButtonProps {
  id: number;
  loadingDeliverOrderId?: number | null;
  onDeliverOrder: (orderId: number) => void;
}

const DeliverOrderButton = ({
  id,
  loadingDeliverOrderId,
  onDeliverOrder,
}: DeliverOrderButtonProps) => {
  return (
    <Button
      height="28px"
      bgColor={AppColors.GREEN_PRIMARY}
      onClick={() => onDeliverOrder(id)}
      loading={loadingDeliverOrderId === id}
      aria-label="Marcar como entregado"
      padding="8px 12px"
    >
      <Check size={12} />
      <Text fontWeight={600} fontSize="14px">
        Marcar como entregado
      </Text>
    </Button>
  );
};

interface CancelOrderButtonProps {
  id: number;
  loadingCancelOrderId: number | null;
  onCancelOrder: (orderId: number) => void;
}

const CancelOrderButton = ({
  id,
  loadingCancelOrderId,
  onCancelOrder,
}: CancelOrderButtonProps) => {
  return (
    <Button
      height="28px"
      bgColor={AppColors.RED_ICON}
      onClick={() => onCancelOrder(id)}
      loading={loadingCancelOrderId === id}
      aria-label="Cancelar pedido"
      padding="8px 12px"
    >
      <Ban size={12} />
      <Text fontWeight={600} fontSize="14px">
        Cancelar pedido
      </Text>
    </Button>
  );
};

const ReassignMenuItemLabel = () => (
  <Flex
    alignItems="center"
    gap="6px"
    width="100%"
    bgColor={AppColors.GREEN_PRIMARY}
    borderRadius="6px"
    paddingX="12px"
    paddingY="6px"
  >
    <Check size={12} color={AppColors.WHITE_SECONDARY} />
    <Text fontWeight={600} fontSize="14px" color={AppColors.WHITE_SECONDARY}>
      Reasignar pedido
    </Text>
  </Flex>
);

const CancelMenuItemLabel = () => (
  <Flex
    alignItems="center"
    gap="6px"
    width="100%"
    bgColor={AppColors.RED_ICON}
    borderRadius="6px"
    paddingX="12px"
    paddingY="6px"
  >
    <Ban size={12} color={AppColors.WHITE_SECONDARY} />
    <Text fontWeight={600} fontSize="14px" color={AppColors.WHITE_SECONDARY}>
      Cancelar pedido
    </Text>
  </Flex>
);

const DeliverMenuItemLabel = () => (
  <Flex
    alignItems="center"
    gap="6px"
    width="100%"
    bgColor={AppColors.GREEN_PRIMARY}
    borderRadius="6px"
    paddingX="12px"
    paddingY="6px"
  >
    <Check size={12} color={AppColors.WHITE_SECONDARY} />
    <Text fontWeight={600} fontSize="14px" color={AppColors.WHITE_SECONDARY}>
      Marcar como entregado
    </Text>
  </Flex>
);

interface OrderActionElementsProps {
  item: any;
  loadingConfirmOrderId: number | null;
  onConfirmOrder: (orderId: number) => void;
  onViewAttempts: (item: any) => void;
  assignDriverBlocked?: boolean;
  reassignDriverBlocked?: boolean;
  onCancelOrder: (orderId: number) => void;
  loadingCancelOrderId: number | null;
  cancelOrderBlocked?: boolean;
  onViewCancelMotive: (item: any) => void;
  onDeliverOrder?: (orderId: number) => void;
  loadingDeliverOrderId?: number | null;
  deliverOrderBlocked?: boolean;
}

const OrderActionElements = ({
  item,
  loadingConfirmOrderId,
  onConfirmOrder,
  onViewAttempts,
  assignDriverBlocked,
  reassignDriverBlocked,
  onCancelOrder,
  loadingCancelOrderId,
  cancelOrderBlocked,
  onViewCancelMotive,
  onDeliverOrder,
  loadingDeliverOrderId,
  deliverOrderBlocked,
}: OrderActionElementsProps) => {
  const hasAttempts = (item?.intentos ?? []).length >= 1;
  const canReassign = canShowReassignAction(item) && !reassignDriverBlocked;
  const canCancelOrder = canCancelOrderStatus(item) && !cancelOrderBlocked;
  const isMostrador = !!item?.esMostrador;
  const canDeliver =
    canMarkSuppliesOrderAsDelivered(item?.estado, isMostrador) &&
    !deliverOrderBlocked &&
    !!onDeliverOrder;

  if (item?.estado === SuppliesOrderStatus.CANCELLED) {
    return (
      <Button
        height="28px"
        padding="8px 12px"
        bgColor={AppColors.BLACK_TEXT_SECONDARY}
        aria-label="Ver motivo"
        onClick={() => onViewCancelMotive(item)}
      >
        <Text fontWeight={600} fontSize="14px" color={AppColors.WHITE_SECONDARY}>
          Ver motivo
        </Text>
      </Button>
    );
  }

  // Pedido por mostrador ya asignado: sin chofer/intentos de la app del
  // chofer, así que las acciones son Reasignar/Marcar como entregado/Cancelar.
  // Son 3 botones (no 2 como el resto de los casos), así que a lo ancho
  // entran uno al lado del otro; recién quedan cortos en pantallas chicas,
  // donde se colapsan al mismo menú "Acciones" que usan los pedidos No
  // entregado. Es un tema de espacio, no de que el pedido "tenga menos
  // opciones" en mobile: por eso conviven ambos layouts y el CSS decide cuál
  // mostrar según breakpoint, en vez de elegir una sola rama por JS.
  if (isMostrador) {
    if (!canReassign && !canDeliver && !canCancelOrder) return null;

    const actionMenuItems = [
      ...(canReassign
        ? [
            {
              label: <ReassignMenuItemLabel />,
              value: "reassign",
              action: () => onConfirmOrder(Number(item?.id)),
            },
          ]
        : []),
      ...(canDeliver
        ? [
            {
              label: <DeliverMenuItemLabel />,
              value: "deliver",
              action: () => onDeliverOrder?.(Number(item?.id)),
            },
          ]
        : []),
      ...(canCancelOrder
        ? [
            {
              label: <CancelMenuItemLabel />,
              value: "cancel",
              action: () => onCancelOrder(Number(item?.id)),
            },
          ]
        : []),
    ];

    return (
      <>
        <Flex
          display={{ base: "none", md: "flex" }}
          alignItems="center"
          justifyContent="flex-end"
          gap="8px"
          flexWrap="wrap"
        >
          {canReassign && (
            <ReassignOrderButton
              id={Number(item?.id)}
              loadingConfirmOrderId={loadingConfirmOrderId}
              onConfirmOrder={onConfirmOrder}
            />
          )}
          {canDeliver && (
            <DeliverOrderButton
              id={Number(item?.id)}
              loadingDeliverOrderId={loadingDeliverOrderId}
              onDeliverOrder={(orderId) => onDeliverOrder?.(orderId)}
            />
          )}
          {canCancelOrder && (
            <CancelOrderButton
              id={Number(item?.id)}
              loadingCancelOrderId={loadingCancelOrderId}
              onCancelOrder={onCancelOrder}
            />
          )}
        </Flex>

        <MenuComponent
          placement="bottom-end"
          trigger={
            <Button
              display={{ base: "flex", md: "none" }}
              height="28px"
              padding="8px 12px"
              aria-label="Acciones"
            >
              <Text fontWeight={600} fontSize="14px">
                Acciones
              </Text>
              <ChevronDown size={12} />
            </Button>
          }
          items={actionMenuItems}
        />
      </>
    );
  }

  if (hasAttempts) {
    if (item?.estado === SuppliesOrderStatus.DELIVERED) return null;

    const actionMenuItems = [
      ...(canReassign
        ? [
            {
              label: <ReassignMenuItemLabel />,
              value: "reassign",
              action: () => onConfirmOrder(Number(item?.id)),
            },
          ]
        : []),
      ...(canCancelOrder
        ? [
            {
              label: <CancelMenuItemLabel />,
              value: "cancel",
              action: () => onCancelOrder(Number(item?.id)),
            },
          ]
        : []),
    ];

    return (
      <Flex
        alignItems={{ base: "flex-end", md: "center" }}
        justifyContent="flex-end"
        flexDir={{ base: "column", md: "row" }}
        gap="8px"
      >
        <Button
          height="28px"
          padding="8px 12px"
          bgColor={AppColors.BLACK_TEXT_SECONDARY}
          aria-label="Ver intentos"
          onClick={() => onViewAttempts(item)}
        >
          <Text fontWeight={600} fontSize="14px" color={AppColors.WHITE_SECONDARY}>
            Ver intentos
          </Text>
        </Button>

        {actionMenuItems.length > 0 && (
          <MenuComponent
            placement="bottom-end"
            trigger={
              <Button height="28px" padding="8px 12px" aria-label="Acciones">
                <Text fontWeight={600} fontSize="14px">
                  Acciones
                </Text>
                <ChevronDown size={12} />
              </Button>
            }
            items={actionMenuItems}
          />
        )}
      </Flex>
    );
  }

  if (item?.estado === SuppliesOrderStatus.PENDING) {
    const hasDriverAssigned = !!item?.chofer;

    if (hasDriverAssigned) {
      if (!canReassign && !canCancelOrder) return null;

      return (
        <Flex alignItems="center" justifyContent="flex-end" gap="8px" flexWrap="wrap">
          {canReassign && (
            <ReassignOrderButton
              id={Number(item?.id)}
              loadingConfirmOrderId={loadingConfirmOrderId}
              onConfirmOrder={onConfirmOrder}
            />
          )}
          {canCancelOrder && (
            <CancelOrderButton
              id={Number(item?.id)}
              loadingCancelOrderId={loadingCancelOrderId}
              onCancelOrder={onCancelOrder}
            />
          )}
        </Flex>
      );
    }

    if (assignDriverBlocked && !canCancelOrder) return null;

    return (
      <Flex alignItems="center" justifyContent="flex-end" gap="8px" flexWrap="wrap">
        {!assignDriverBlocked && (
          <ConfirmOrderButton
            id={Number(item?.id)}
            loadingConfirmOrderId={loadingConfirmOrderId}
            onConfirmOrder={onConfirmOrder}
          />
        )}
        {canCancelOrder && (
          <CancelOrderButton
            id={Number(item?.id)}
            loadingCancelOrderId={loadingCancelOrderId}
            onCancelOrder={onCancelOrder}
          />
        )}
      </Flex>
    );
  }

  if (canCancelOrder) {
    return (
      <CancelOrderButton
        id={Number(item?.id)}
        loadingCancelOrderId={loadingCancelOrderId}
        onCancelOrder={onCancelOrder}
      />
    );
  }

  return null;
};

export const FuelSlotRequestOrdersTable = ({
  orders,
  loadingConfirmOrderId,
  onConfirmOrder,
  showEditOrders,
  onEditOrders,
  assignDriverBlocked,
  reassignDriverBlocked,
  onCancelOrder,
  loadingCancelOrderId,
  cancelOrderBlocked,
  onDeliverOrder,
  loadingDeliverOrderId,
  deliverOrderBlocked,
}: Props) => {
  const [openAttempts, setOpenAttempts] = useState(false);
  const [selectedAttemptItem, setSelectedAttemptItem] = useState<
    FuelOrdersTableComputedItem | null
  >(null);

  const [openCancelMotive, setOpenCancelMotive] = useState(false);
  const [selectedCancelMotiveItem, setSelectedCancelMotiveItem] = useState<
    FuelOrdersTableComputedItem | null
  >(null);

  const handleViewCancelMotive = (item: any) => {
    setSelectedCancelMotiveItem(item);
    setOpenCancelMotive(true);
  };

  const items: FuelOrdersTableComputedItem[] = useMemo(() => {
    const mapped = (orders ?? []).map((o) => {
        const nroPedido = o?.nroPedido || `Pedido ${o?.id}`;
        const litros = `${new Intl.NumberFormat("es-AR").format(
          o?.cantidadTotal ?? 0,
        )} litros`;
        const despacho = o?.nombreDespacho || o?.destino || "Sin despacho";
        const status = o?.estado as unknown as SuppliesOrderStatus;
        const intentos = o?.intentos ?? [];
        const maxIntento = Math.max(
          0,
          ...intentos.map((a) => Number(a?.nroIntento ?? 0)),
        );

        let currentIntento = 0;
        if (status === SuppliesOrderStatus.NOT_DELIVERED) {
          currentIntento = maxIntento;
        } else if (status === SuppliesOrderStatus.PENDING && intentos.length >= 1) {
          currentIntento = maxIntento + 1;
        }

        return {
          ...o,
          nroPedido,
          litros,
          despacho,
          status,
          intentos,
          currentIntento,
        };
      }) as FuelOrdersTableComputedItem[];

    return mapped;
  }, [orders]);

  const handleViewAttempts = (item: any) => {
    setSelectedAttemptItem(item);
    setOpenAttempts(true);
  };

  return (
    <Flex flexDir="column" gap="8px" paddingBottom="12px">
      <Flex alignItems="center" justifyContent="space-between">
        <Text fontWeight={700}>Pedidos</Text>

        {showEditOrders && (
          <Button height="28px" onClick={onEditOrders} padding="8px 12px">
            <PenLine size={12} />
            <Text fontWeight={600} fontSize="14px">
              Editar pedidos
            </Text>
          </Button>
        )}
      </Flex>

      <Flex
        flexDir="column"
        border={`1px solid ${AppColors.WHITE_BORDER_1}`}
        borderRadius="8px"
        overflow="hidden"
        maxH="320px"
        overflowY="auto"
      >
        {items.map((item, index) => (
          <Flex
            key={item?.id ?? index}
            alignItems="center"
            justifyContent="flex-start"
            paddingX="20px"
            paddingY="16px"
            borderTop={
              index === 0 ? "none" : `1px solid ${AppColors.WHITE_BORDER_1}`
            }
            width="100%"
          >
            <Grid
              templateColumns={{
                base: "auto 110px minmax(0, 1fr) auto",
                md: "auto 150px minmax(0, 1fr) auto",
              }}
              alignItems="center"
              width="100%"
              minW={0}
            >
              <GridItem
                borderRight={`1px solid ${AppColors.WHITE_BORDER_1}`}
                paddingRight={{ base: "12px", md: "20px" }}
              >
                <Text fontWeight={700} fontSize="14px">
                  {item?.nroPedido}
                </Text>
              </GridItem>

              <GridItem
                borderRight={`1px solid ${AppColors.WHITE_BORDER_1}`}
                paddingX="24px"
              >
                <Text fontWeight={600} fontSize="14px" color={AppColors.GRAY_TEXT}>
                  {item?.litros}
                </Text>
              </GridItem>

              <GridItem
                borderRight={`1px solid ${AppColors.WHITE_BORDER_1}`}
                paddingX="24px"
                minW={0}
                overflow="hidden"
              >
                <Flex
                  alignItems={{ base: "flex-start", md: "center" }}
                  flexDir={{ base: "column", md: "row" }}
                  columnGap="24px"
                  rowGap="8px"
                  minW={0}
                  flexWrap="wrap"
                >
                  <Text
                    fontWeight={600}
                    fontSize="14px"
                    color={AppColors.GRAY_TEXT}
                    whiteSpace="nowrap"
                    overflow="hidden"
                    title={item?.despacho}
                    textOverflow="ellipsis"
                    minW={0}
                  >
                    {item?.despacho}
                  </Text>

                  <Text
                    fontWeight={600}
                    fontSize="14px"
                    color={AppColors.GRAY_TEXT}
                    whiteSpace="nowrap"
                    overflow="hidden"
                    title={
                      item?.esMostrador
                        ? "Por mostrador"
                        : item?.chofer ?? "Sin chofer"
                    }
                    textOverflow="ellipsis"
                    minW={0}
                  >
                    {item?.esMostrador
                      ? "Por mostrador"
                      : item?.chofer
                        ? `Chofer: ${item?.chofer}`
                        : "Sin chofer"}
                  </Text>

                  <Flex alignItems="center" gap="8px" flexWrap="wrap" maxW="100%">
                    <RequestStatusBadge status={item?.status} />
                    {item.currentIntento >= 1 && (item?.intentos ?? []).length >= 1 && (
                        <Badge bg={AppColors.YELLOW_SECONDARY} >
                          <Text
                            paddingY="4px"
                            paddingX="8px"
                            fontWeight={600}
                            fontSize="12px"
                            color={AppColors.WHITE_SECONDARY}
                          >
                            Intento {item.currentIntento}
                          </Text>
                        </Badge>
                      )}
                  </Flex>
                </Flex>
              </GridItem>

              <GridItem
                justifySelf="end"
                paddingLeft="12px"
                maxW={{ base: "240px", md: "unset" }}
              >
                <OrderActionElements
                  item={item}
                  loadingConfirmOrderId={loadingConfirmOrderId}
                  onConfirmOrder={onConfirmOrder}
                  onViewAttempts={handleViewAttempts}
                  assignDriverBlocked={assignDriverBlocked}
                  reassignDriverBlocked={reassignDriverBlocked}
                  onCancelOrder={onCancelOrder}
                  loadingCancelOrderId={loadingCancelOrderId}
                  cancelOrderBlocked={cancelOrderBlocked}
                  onViewCancelMotive={handleViewCancelMotive}
                  onDeliverOrder={onDeliverOrder}
                  loadingDeliverOrderId={loadingDeliverOrderId}
                  deliverOrderBlocked={deliverOrderBlocked}
                />
              </GridItem>
            </Grid>
          </Flex>
        ))}
      </Flex>

      {selectedAttemptItem && (
        <SuppliesOrderAttemptsModal
          open={openAttempts}
          setOpen={(flag) => {
            setOpenAttempts(flag);
            if (!flag) setSelectedAttemptItem(null);
          }}
          pedidoLabel="N° Pedido: -"
          pedidoId={selectedAttemptItem?.id ?? "-"}
          toneladasOrLitrosLabel={`Litros: ${selectedAttemptItem?.litros ?? "-"}`}
          despachoLabel={`Despacho: ${selectedAttemptItem?.despacho ?? "-"}`}
          attempts={selectedAttemptItem?.intentos ?? []}
          showNextAttemptPlaceholder={
            (selectedAttemptItem?.estado === SuppliesOrderStatus.PENDING ||
              normalizeStatus(selectedAttemptItem?.estado) ===
                normalizeStatus(SuppliesOrderStatus.IN_TRANSIT) ||
              normalizeStatus(selectedAttemptItem?.estado).includes("camino") ||
              normalizeStatus(selectedAttemptItem?.estado).includes("transito")) &&
            (selectedAttemptItem?.intentos ?? []).length >= 1
          }
        />
      )}

      {selectedCancelMotiveItem && (
        <SuppliesOrderCancelMotiveModal
          open={openCancelMotive}
          setOpen={(flag) => {
            setOpenCancelMotive(flag);
            if (!flag) setSelectedCancelMotiveItem(null);
          }}
          pedidoLabel={selectedCancelMotiveItem?.nroPedido}
          motivo={selectedCancelMotiveItem?.motivoCancelacion}
          fecha={selectedCancelMotiveItem?.fechaCancelacion}
          usuario={selectedCancelMotiveItem?.usuarioCancela}
        />
      )}
    </Flex>
  );
};
